import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useCurriculumStore } from '../../stores/curriculumStore';
import type { CurriculumPlan } from '../../types';

const planSchema = z.object({
  subjectId: z.string().min(1, 'Subject is required'),
  yearGroupId: z.string().min(1, 'Year group is required'),
  lessonsPerWeek: z.number().min(1, 'Must have at least 1 lesson per week'),
  teachingModel: z.enum(['whole-class', 'sets', 'bands', 'options']),
  setCount: z.number().optional(),
  constraints: z.object({
    requiresDoubleLessons: z.boolean(),
    doubleCount: z.number().optional(),
    requiresSpecialistRoom: z.boolean(),
    requiredRoomType: z.string().optional(),
    isElective: z.boolean(),
    optionBlock: z.string().optional(),
  }),
});

type PlanFormData = z.infer<typeof planSchema>;

interface PlanFormProps {
  plan?: CurriculumPlan;
  onSubmit: (data: CurriculumPlan) => void;
  onCancel: () => void;
}

const yearGroups = [
  { value: 'year-7', label: 'Year 7' },
  { value: 'year-8', label: 'Year 8' },
  { value: 'year-9', label: 'Year 9' },
  { value: 'year-10', label: 'Year 10' },
  { value: 'year-11', label: 'Year 11' },
  { value: 'year-12', label: 'Year 12' },
  { value: 'year-13', label: 'Year 13' },
];

const teachingModels = [
  { value: 'whole-class', label: 'Whole Class' },
  { value: 'sets', label: 'Sets (Ability Groups)' },
  { value: 'bands', label: 'Bands (Mixed Groups)' },
  { value: 'options', label: 'Options (Elective)' },
];

const roomTypes = [
  { value: 'classroom', label: 'Classroom' },
  { value: 'laboratory', label: 'Laboratory' },
  { value: 'ict', label: 'ICT Suite' },
  { value: 'art', label: 'Art Studio' },
  { value: 'music', label: 'Music Room' },
  { value: 'dt', label: 'Design Technology' },
  { value: 'gym', label: 'Gymnasium' },
  { value: 'sports-hall', label: 'Sports Hall' },
];

export const PlanForm: React.FC<PlanFormProps> = ({ plan, onSubmit, onCancel }) => {
  const { subjects, optionBlocks } = useCurriculumStore();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    defaultValues: {
      subjectId: plan?.subjectId || '',
      yearGroupId: plan?.yearGroupId || '',
      lessonsPerWeek: plan?.lessonsPerWeek || 3,
      teachingModel: plan?.teachingModel || 'whole-class',
      setCount: plan?.setCount || undefined,
      constraints: {
        requiresDoubleLessons: plan?.constraints.requiresDoubleLessons || false,
        doubleCount: plan?.constraints.doubleCount || undefined,
        requiresSpecialistRoom: plan?.constraints.requiresSpecialistRoom || false,
        requiredRoomType: plan?.constraints.requiredRoomType || undefined,
        isElective: plan?.constraints.isElective || false,
        optionBlock: plan?.constraints.optionBlock || undefined,
      },
    },
  });

  const teachingModel = watch('teachingModel');
  const requiresDoubleLessons = watch('constraints.requiresDoubleLessons');
  const requiresSpecialistRoom = watch('constraints.requiresSpecialistRoom');
  const isElective = watch('constraints.isElective');

  const handleFormSubmit = (data: PlanFormData) => {
    onSubmit(data as CurriculumPlan);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="subjectId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.subjectId}>
                <InputLabel>Subject *</InputLabel>
                <Select {...field} label="Subject *">
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name} ({subject.code})
                    </MenuItem>
                  ))}
                </Select>
                {errors.subjectId && <FormHelperText>{errors.subjectId.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="yearGroupId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.yearGroupId}>
                <InputLabel>Year Group *</InputLabel>
                <Select {...field} label="Year Group *">
                  {yearGroups.map((yg) => (
                    <MenuItem key={yg.value} value={yg.value}>
                      {yg.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.yearGroupId && <FormHelperText>{errors.yearGroupId.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="lessonsPerWeek"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="Lessons Per Week"
                fullWidth
                error={!!errors.lessonsPerWeek}
                helperText={errors.lessonsPerWeek?.message}
                required
                onChange={(e) => field.onChange(Number.parseInt(e.target.value))}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="teachingModel"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.teachingModel}>
                <InputLabel>Teaching Model *</InputLabel>
                <Select {...field} label="Teaching Model *">
                  {teachingModels.map((tm) => (
                    <MenuItem key={tm.value} value={tm.value}>
                      {tm.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.teachingModel && <FormHelperText>{errors.teachingModel.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        {teachingModel === 'sets' && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="setCount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Number of Sets"
                  fullWidth
                  helperText="How many ability sets for this subject?"
                  onChange={(e) => field.onChange(Number.parseInt(e.target.value))}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Constraints
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="constraints.isElective"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Elective Subject (students choose)"
              />
            )}
          />
        </Grid>

        {isElective && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="constraints.optionBlock"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Option Block</InputLabel>
                  <Select {...field} label="Option Block">
                    <MenuItem value="">None</MenuItem>
                    {optionBlocks.map((block) => (
                      <MenuItem key={block.id} value={block.id}>
                        {block.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Controller
            name="constraints.requiresDoubleLessons"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Requires Double Lessons"
              />
            )}
          />
        </Grid>

        {requiresDoubleLessons && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="constraints.doubleCount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Number of Double Lessons"
                  fullWidth
                  helperText="Per week"
                  onChange={(e) => field.onChange(Number.parseInt(e.target.value))}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Controller
            name="constraints.requiresSpecialistRoom"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Requires Specialist Room"
              />
            )}
          />
        </Grid>

        {requiresSpecialistRoom && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="constraints.requiredRoomType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Room Type</InputLabel>
                  <Select {...field} label="Room Type">
                    {roomTypes.map((rt) => (
                      <MenuItem key={rt.value} value={rt.value}>
                        {rt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {plan ? 'Update' : 'Create'} Plan
        </Button>
      </Box>
    </form>
  );
};
